import React from 'react'
import { FormControl, Select, MenuItem, ListItemText } from '@mui/material'
import { Checkbox, Typography } from '@mui/material'

import { selectInputStyles } from './select-input.styles'
import { withStyles } from '@mui/styles'

/**
 * @typedef {object} SelectOption
 * @property {string | number} key - El valor único de la opción.
 * @property {string} value - El texto que se muestra para la opción.
 */

/**
 * @typedef {object} SelectInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {SelectOption[]} [values=[]] - Array de opciones disponibles para seleccionar.
 * @property {boolean} [multiple=false] - Si es `true`, permite selección múltiple con checkboxes.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra el valor como texto de solo lectura.
 * @property {object} [MenuProps] - Props adicionales para el componente Menu de Material-UI.
 * @property {string} [title] - Título que se muestra encima del select.
 * @property {string | number | Array<string|number>} [value] - El valor seleccionado. Array si `multiple` es `true`.
 * @property {string} name - Nombre del campo para el evento onChange.
 * @property {function(object): void} onChange - Callback que se ejecuta cuando la selección cambia.
 * @property {string} [placeholder] - Texto de marcador de posición.
 * @property {'small' | 'medium'} [fontSize] - Tamaño de la fuente para el título.
 * @property {boolean} [required] - Si es `true`, marca el campo como requerido.
 * @property {boolean} [disabled] - Si es `true`, deshabilita el select.
 */

/**
 * Componente de selección (dropdown) con soporte para selección simple y múltiple.
 * 
 * Proporciona un select personalizado con:
 * - **Selección simple o múltiple**: Checkboxes para selección múltiple
 * - **Modo solo lectura**: Visualización del valor seleccionado como texto
 * - **Valores personalizados**: Soporte para cualquier tipo de clave (string/number)
 * - **Renderizado personalizado**: Muestra valores separados por comas en modo múltiple
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [category, setCategory] = useState('');
 *   const [tags, setTags] = useState([]);
 * 
 *   const categories = [
 *     { key: 1, value: 'Electrónica' },
 *     { key: 2, value: 'Hogar' },
 *   ];
 * 
 *   return (
 *     <>
 *       <SelectInput
 *         name="category"
 *         title="Categoría"
 *         values={categories}
 *         value={category}
 *         onChange={(e) => setCategory(e.target.value)}
 *       />
 *       <SelectInput
 *         name="tags"
 *         title="Etiquetas"
 *         values={categories}
 *         value={tags}
 *         onChange={(e) => setTags(e.target.value)}
 *         multiple
 *       />
 *     </>
 *   );
 * };
 * 
 * @param {SelectInputProps} props - Las props del componente SelectInput.
 */
export const SelectInput = React.memo(
  withStyles(selectInputStyles)(
    ({ classes, values = [], multiple = false, readOnly = false, MenuProps, ...props }) => {
      const value = props.value || []
      const typographyVariant = props.fontSize === 'small' ? '2' : '1'

      const selectValues = () => {
        return values.map((item, i) => (
          <MenuItem key={i} value={item.key}>
            {multiple ? (
              <>
                <Checkbox checked={value.indexOf(item.key) > -1} />
                <ListItemText primary={item.value} />
              </>
            ) : (
              item.value
            )}
          </MenuItem>
        ))
      }

      const multipleProps =
        multiple && values.length
          ? {
              renderValue: (selected) =>
                selected
                  .map((item) => {
                    let found = values.find((value) => value.key === item)
                    return found ? found.value : 'Unknown'
                  })
                  .join(', '),
            }
          : {}

      return (
        <FormControl
          variant="outlined"
          className={classes.root}
          required={props.required}
          disabled={props.disabled}
        >
          {props.title && (
            <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
              {props.title}
            </Typography>
          )}
          {readOnly ? (
            <Typography variant={`body${typographyVariant}`} color="textSecondary">
              {value && values ? values.find((option) => option.key === value)?.value : '--'}
            </Typography>
          ) : (
            <Select
              autoWidth
              className={classes.input}
              name={props.name}
              value={value}
              multiple={multiple}
              onChange={props.onChange}
              placeholder={props.placeholder}
              MenuProps={MenuProps}
              {...multipleProps}
            >
              {selectValues()}
            </Select>
          )}
        </FormControl>
      )
    }
  )
)
